/**
 * O rodapé dos três sites (mapa.voto, replay e pesquisas; ORD-F-145, pedido do responsável): o conteúdo num lugar só.
 * Dois desenhos do MESMO modelo e do MESMO CSS (`SiteFooter.css`): o componente `SiteFooter` (páginas com JS) e
 * `siteFooterHtml` (páginas estáticas, gravado no build pelo plugin `scripts/vite/site-footer.ts`). Sem script nem fonte
 * de terceiro; os ícones são SVG próprios, no CSS.
 */

export interface FooterLink {
  id: string;
  href: string;
  /** O que se lê. */
  label: string;
  /** O nome acessível (diz que abre em nova aba). */
  aria?: string;
  /** Link para fora (nova aba). */
  external?: boolean;
}

/** Endereço do site principal: as páginas institucionais moram nele. */
export const MAIN_SITE = 'https://mapa.voto';

export const FOOTER_SOCIAL: readonly FooterLink[] = [
  {
    id: 'instagram',
    href: 'https://instagram.com/mapa.voto',
    label: '@mapa.voto',
    aria: 'Instagram: @mapa.voto (abre em nova aba)',
    external: true,
  },
  {
    id: 'youtube',
    href: 'https://www.youtube.com/@mapa.voto.26',
    label: '@mapa.voto.26',
    aria: 'YouTube: @mapa.voto.26 (abre em nova aba)',
    external: true,
  },
  { id: 'email', href: 'mailto:contato@mapa.voto', label: 'contato@mapa.voto', aria: 'E-mail: contato@mapa.voto' },
];

/** As páginas que já existiam no rodapé (ORD-F-145: continuam). */
export const FOOTER_PAGES: readonly FooterLink[] = [
  { id: 'sobre', href: '/sobre', label: 'Sobre' },
  { id: 'metodologia', href: '/metodologia', label: 'Como funciona' },
  { id: 'privacidade', href: '/privacidade', label: 'Privacidade' },
];

export const FOOTER_TEXT = {
  powered: 'powered by mapa.voto',
  social: 'Redes e contato',
  pages: 'Páginas do site',
} as const;

/** O endereço com a base do site (vazia no próprio mapa.voto; o endereço dele nos outros dois sites). */
export const withBase = (base: string, href: string): string =>
  href.startsWith('/') ? `${base.replace(/\/$/, '')}${href}` : href;

const esc = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

function linkHtml(l: FooterLink, cls: string, icon: boolean, base: string): string {
  const attrs = [
    `class="${cls}${icon ? ` ${cls}--${l.id}` : ''}"`,
    `href="${esc(withBase(base, l.href))}"`,
    l.external ? 'target="_blank" rel="noopener noreferrer"' : '',
    l.aria ? `aria-label="${esc(l.aria)}"` : '',
    `data-testid="site-footer-${l.id}"`,
  ]
    .filter(Boolean)
    .join(' ');
  const ico = icon ? '<span class="site-footer__icon" aria-hidden="true"></span>' : '';
  return `<a ${attrs}>${ico}<span class="site-footer__label">${esc(l.label)}</span></a>`;
}

/**
 * O rodapé em HTML, para as páginas sem JS (mesma árvore e classes do `SiteFooter`). `div` quando vai dentro do rodapé que
 * a página já tem (um só marco `contentinfo`), como nas páginas estáticas.
 */
export function siteFooterHtml(base: string, tag: 'footer' | 'div' = 'div'): string {
  const social = FOOTER_SOCIAL.map((l) => `<li>${linkHtml(l, 'site-footer__social-link', true, base)}</li>`).join('');
  const pages = FOOTER_PAGES.map((l) => `<li>${linkHtml(l, 'site-footer__page', false, base)}</li>`).join('');
  return (
    `<${tag} class="site-footer" data-testid="site-footer">` +
    `<ul class="site-footer__social" aria-label="${FOOTER_TEXT.social}">${social}</ul>` +
    `<nav class="site-footer__nav" aria-label="${FOOTER_TEXT.pages}"><ul class="site-footer__pages">${pages}</ul></nav>` +
    `<p class="site-footer__powered"><a class="site-footer__home" href="${esc(MAIN_SITE)}" data-testid="site-footer-home">${FOOTER_TEXT.powered}</a></p>` +
    `</${tag}>`
  );
}
